import { getTextStyle } from "@certn/cascadia-react/foundations/typography/styles";
import {
  formatAddress,
  formatCurrency,
  formatDate,
  formatDurationLength,
  formatName,
  inferCurrency,
  parseName,
} from "@certn/utils";
import { css } from "@emotion/css";
import {
  Category,
  GroupLayout,
  JsonSchema,
  JsonSchema7,
  Layout,
  UISchemaElement,
  VerticalLayout,
  isCategory,
  isGroup,
} from "@jsonforms/core";
import Ajv from "ajv";
import { TFunction } from "i18next";
import get from "lodash.get";

import { ReportLayout } from "./types";
import { CascadiaLayoutProps } from "./wrappers/withCascadiaLayoutProps";

export function getPath(object: any, path: string, defval = null) {
  const parts = typeof path === "string" ? path.split(".") : path;
  return parts.reduce((xs, x) => (xs && xs[x] ? xs[x] : defval), object);
}

export function isUISchemaElement(
  uischema: unknown
): uischema is UISchemaElement {
  return (
    typeof uischema === "object" &&
    (uischema as UISchemaElement).type !== undefined
  );
}

export function isLayout(uischema: unknown): uischema is Layout {
  if (!isUISchemaElement(uischema)) {
    return false;
  }

  return true;
}

export function isVerticalLayout(
  uischema: unknown
): uischema is VerticalLayout {
  if (!isLayout(uischema)) {
    return false;
  }

  const verticalLayout = uischema as VerticalLayout;

  return (
    verticalLayout.type === "VerticalLayout" &&
    Array.isArray(verticalLayout.elements)
  );
}

export function hasLabel(uischema: unknown): uischema is { label?: string } {
  return (
    typeof uischema === "object" && uischema !== null && "label" in uischema
  );
}

export function isCategoryLayout(uischema: unknown): uischema is Category {
  if (!isLayout(uischema)) {
    return false;
  }

  if (!isCategory(uischema)) {
    return false;
  }

  return true;
}

export function isGroupLayout(uischema: unknown): uischema is GroupLayout {
  if (!isLayout(uischema)) {
    return false;
  }

  if (!isGroup(uischema)) {
    return false;
  }

  return true;
}

export function isReportLayout(uischema: unknown): uischema is ReportLayout {
  if (!isLayout(uischema)) {
    return false;
  }

  if (uischema.type !== "ReportLayout") {
    return false;
  }

  return true;
}

export function isTableLayout(uischema: unknown): uischema is GroupLayout {
  return (
    isLayout(uischema) &&
    uischema.type === "TableLayout" &&
    Array.isArray(uischema.elements)
  );
}

export function isTableRowLayout(uischema: unknown): uischema is Layout {
  return (
    isLayout(uischema) &&
    uischema.type === "TableRowLayout" &&
    Array.isArray(uischema.elements)
  );
}

export function isButtonGroupLayout(uischema: unknown): uischema is Layout {
  return (
    isLayout(uischema) &&
    uischema.type === "ButtonGroupLayout" &&
    Array.isArray(uischema.elements)
  );
}

export const sharedStyles = {
  input: css(getTextStyle("input/text-field"), {
    textAlign: "left",
    border: "none",
    borderRadius: "var(--border-radius-2)",
    outline: "none",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    paddingRight: "var(--space-3)",
    paddingTop: "var(--space-2)",
    color: "var(--text-base)",
    backgroundColor: "var(--background-input)",
    boxShadow: `inset 0 0 0 1px var(--border-input)`,
    "::placeholder": { color: "var(--text-placeholder)" },
    "&[data-hovered]": {
      boxShadow: `inset 0 0 0 1px var(--border-input-hover)`,
    },
    "&[data-focused]": {
      boxShadow: "var(--focus-input)",
    },
    "&[data-invalid]": css({
      boxShadow: `inset 0 0 0 1px var(--border-danger-input)`,
      "&[data-focused]": {
        boxShadow: "var(--focus-input-error)",
      },
    }),
    "&[data-disabled], &[aria-disabled]": {
      boxShadow: `inset 0 0 0 1px var(--border-input-disabled)`,
      backgroundColor: "var(--background-input-disabled)",
      color: "var(--text-disabled)",
      "::placeholder": { color: "var(--text-disabled)" },
    },
    "&input&:read-only": {
      color: "var(--text-weak)",
    },
  }),
  card: css({
    flexDirection: "row-reverse",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "red",
    borderRadius: "var(--border-radius-3)",
    alignItems: "center",
    padding: "var(--space-3)",
    justifyContent: "space-between",
  }),
  field: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
    minWidth: 80,
    // maxWidth: 416,
    borderColor: "var(--border-base)",
    "&[data-hovered]": { borderColor: "var(--border-input-hover)" },
    "&[data-selected]": { borderColor: "var(--border-input-active)" },
    "&[data-disabled]": {
      borderColor: "var(--border-input-disabled)",
    },
    "&[data-focused]": {
      boxShadow: "var(--focus-action)",
    },
    "&[data-label-orientation='horizontal']": {
      display: "grid",
      gridTemplateColumns: "160px 1fr",
      alignItems: "flex-start",
      columnGap: "var(--space-4)",
    },
    "&[data-label-orientation='horizontal'] > :first-child": {
      gridColumn: "1",
    },
    "&[data-label-orientation='horizontal'] > :nth-child(n+2)": {
      gridColumn: "2",
    },
  }),
};

export function strToKebabCase(str: string): string {
  return str.toLowerCase().replace(/\s/g, "-");
}

export function getKeyFromScope(scope: string): string {
  // get data key from scope
  // ie: get "sources" from #/properties/sources
  const key = scope.split("/").pop();
  return key === undefined ? "" : key;
}

export function handleMissingAndBlankData(
  data: any,
  showZero: boolean = true,
  showFalse: boolean = true
) {
  if (data === null || data === undefined || data === "") return "--";
  data = data.toString();

  if (data === "0" && !showZero) return "--";
  if (data === "false" && !showFalse) return "--";

  return data;
}

export function formatData(
  data: any,
  format: string,
  t?: TFunction<"translation", undefined>
) {
  function is_valid(data: any, format: string) {
    if (data === "" || data === null || data === undefined) return false;

    if (["percentage", "currency", "monthly"].includes(format)) {
      if (parseFloat(data).toString() === "NaN") return false;
    }

    if (
      ["date", "date-month-granularity"].includes(format) &&
      typeof data !== "string" &&
      !(data instanceof Date)
    )
      return false;

    if (
      format === "date-with-resolution" &&
      !(
        typeof data === "object" &&
        data !== null &&
        "date" in data &&
        "resolution" in data
      )
    ) {
      return false;
    }

    return true;
  }

  if (!is_valid(data, format)) return data;

  if (format === "percentage") return `${parseFloat(data).toFixed(0)}%`;

  if (format === "currency")
    return formatCurrency(data, "en-US", inferCurrency("en-US"));

  if (format === "monthly") {
    const monthly_cost = formatCurrency(data, "en-US", inferCurrency("en-US"));
    if (t) return t("{{monthly_cost}}/mo", { monthly_cost });
    else return `${monthly_cost}/mo`;
  }

  if (format === "name") return formatName(parseName(data), "full");

  if (format === "duration-length") {
    const allLabels: string[] = [];
    const keys = ["years", "months", "days"];
    if (t) {
      allLabels.push(t("years"), t("months"), t("days"));
    } else {
      allLabels.push("years", "months", "days");
    }
    const filteredLabels = keys
      .map((key, i) => ({ key, label: allLabels[i] }))
      .filter(({ key }) => key in data);

    const requiredLabels = filteredLabels.map((item) => item.label);

    const processedData = requiredLabels.map((label) => ({
      key: label,
      value: data[label],
    }));

    return formatDurationLength(processedData);
  }

  if (["date", "date-month-granularity"].includes(format)) {
    // make sure it will always come from the BE in ISO format
    const isRedacted = typeof data === "string" && data.match(/^\d{4}-XX-XX$/);
    if (isRedacted) return data;
    if (data instanceof Date)
      return formatDate(data, format === "date" ? "PP" : "MMM, yyyy");
    const numDashes = data.match(/-/g)?.length || 0;

    if (numDashes === 1 || format === "date-month-granularity") {
      return formatDate(data, "MMM, yyyy");
    }
    if (numDashes === 2) return formatDate(data, "PP");
  }

  if (format === "date-with-resolution") {
    return formatDate(data.date, data.resolution, data.date);
  }

  if (format === "address") return formatAddress(data);

  return data;
}

export function mapToDisplayValue(value: any, displayMapping?: any) {
  if (displayMapping && value in displayMapping) {
    return displayMapping[value];
  }
  return value;
}

function getNestedSchemaPath(path: string[], pathPrefix: string[] = []) {
  const nestedStartIndex =
    pathPrefix.length > 0
      ? path.length -
        [...path]
          .reverse()
          .findIndex((item) => item === pathPrefix.slice(-1)[0])
      : 0;
  const nestedPath = path.slice(nestedStartIndex);
  return nestedPath.flatMap((path) =>
    Number.isInteger(Number(path)) ? ["items"] : ["properties", path]
  );
}

function ruleThenMatchesFieldPath(
  pathPrefix: string[],
  rule: any,
  fieldPath: string[]
) {
  const path = getNestedSchemaPath(fieldPath, pathPrefix);
  const schema = get(rule, ["then", ...path]);
  return schema !== undefined && !schema.properties;
}

function ruleIfIsTrue(path: string[], rule: any, rootValues: object) {
  const ajv = new Ajv({ strict: false });
  const validate = ajv.compile(rule.if);
  const values = get(rootValues, path, rootValues);
  return validate(values);
}

export function computeConditionalOneOf(
  rootSchema: JsonSchema,
  rootValues: object,
  fieldPath: string[]
) {
  const allOfRules = [
    ...(rootSchema.allOf?.map<[string[], JsonSchema]>((item) => [[], item]) ||
      []),
    ...fieldPath.flatMap((path, index) => {
      const pathPrefix = fieldPath.slice(0, index + 1);
      const allOfs: JsonSchema[] | undefined = get(rootSchema, [
        ...getNestedSchemaPath(pathPrefix),
        "allOf",
      ]);
      return (
        allOfs?.map<[string[], JsonSchema]>((allOf) => [pathPrefix, allOf]) ||
        []
      );
    }),
  ]
    .filter(([_, rule]) => Boolean(rule))
    .filter(([pathPrefix, rule]) =>
      ruleThenMatchesFieldPath(pathPrefix, rule, fieldPath)
    )
    .filter(([pathPrefix, rule]) => ruleIfIsTrue(pathPrefix, rule, rootValues));

  if (allOfRules.length === 1) {
    const [pathPrefix, rule] = allOfRules[0];
    const path = getNestedSchemaPath(fieldPath, pathPrefix);
    return get((rule as JsonSchema7).then, [...path, "oneOf"]) as
      | JsonSchema7[]
      | undefined;
  }
}

export function shouldLayoutContentBeSummary(
  uischema: CascadiaLayoutProps["uischema"],
  data: any
) {
  const name = (uischema as any)["name"];
  const isEditableFieldPath = uischema.options?.isEditableFieldPath;
  if (isEditableFieldPath && name) {
    return get(data, isEditableFieldPath) !== true;
  }
  return false;
}
